import { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QuotaExceededModal, extractQuotaDetail, type QuotaExceededDetail } from '../../components/QuotaExceededModal'; // Phase 3d.1 — quota modal
import { packageEditor, assets } from '../../api';
import {
  type PackageDocument,
  type Layout,
  type LayoutKind,
  type Scene,
  type Region,
  type Block,
  type BlockKind,
  type TextBlock,
  type ImageBlock,
  type VideoBlock,
  type WebBlock,
  type ClockBlock,
  type YouTubeBlock,
  type WeatherBlock,
  type PdfBlock,
  type RssBlock,
  type CustomHtmlBlock,
  slotsForLayout,
  newBlock,
  blockLabel,
} from '../../content/document_schema';
import { AssetPicker } from '../../components/AssetPicker';

const LAYOUTS: Array<{ kind: LayoutKind; label: string }> = [
  { kind: 'fullscreen',    label: 'Fullscreen' },
  { kind: 'split-h',       label: 'Split — horizontal' },
  { kind: 'split-v',       label: 'Split — vertical' },
  { kind: 'thirds-h',      label: 'Thirds — horizontal' },
  { kind: 'thirds-v',      label: 'Thirds — vertical' },
  { kind: 'pip',           label: 'Picture-in-picture' },
  { kind: 'ticker-bottom', label: 'Main + ticker' },
  { kind: 'freeform',      label: 'Freeform' },
];

const BLOCK_KINDS: BlockKind[] = [
  'text', 'image', 'video', 'web', 'clock',
  'weather', 'rss', 'youtube', 'pdf', 'custom-html',
];

// Block kinds that render fully on the Pi today (Phase 2a)
const PHASE_SUPPORTED: BlockKind[] = ['text', 'image', 'video', 'web', 'clock', 'youtube', 'weather', 'pdf', 'rss', 'custom-html'];

export function PackageEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<PackageDocument | null>(null);
  const [pkgName, setPkgName] = useState('');
  const [activeSceneId, setActiveSceneId] = useState<string | null>(null);
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [quotaDetail, setQuotaDetail] = useState<QuotaExceededDetail | null>(null); // Phase 3d.1 — quota modal
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!id) return;
    packageEditor.getDraft(id).then((r) => {
      setDoc(r.doc);
      setPkgName(r.package.name);
      const firstScene = r.doc.scenes[0];
      if (firstScene) {
        setActiveSceneId(firstScene.id);
        if (firstScene.regions[0]) setSelectedRegionId(firstScene.regions[0].id);
      }
    }).catch(() => setErr('Failed to load package.'));
  }, [id]);

  if (!doc) return <div>Loading…</div>;

  const activeScene = doc.scenes.find((s) => s.id === activeSceneId) ?? doc.scenes[0];
  const selectedRegion = activeScene.regions.find((r) => r.id === selectedRegionId) ?? null;

  function updateDoc(updater: (d: PackageDocument) => PackageDocument) {
    setDoc((cur) => cur && updater(cur));
    setDirty(true);
  }

  function updateActiveScene(updater: (s: Scene) => Scene) {
    updateDoc((d) => ({
      ...d,
      scenes: d.scenes.map((s) => (s.id === activeScene.id ? updater(s) : s)),
    }));
  }

  /* ------------ Scene operations ------------ */
  function addScene() {
    const newId = `scene-${Date.now()}`;
    const slots = slotsForLayout(doc!.layout);
    const regions: Region[] = doc!.layout.kind === 'freeform'
      ? []
      : slots.map((slot, i) => ({
          id: `region-${Date.now()}-${i}`,
          slot,
          block: newBlock('text'),
        }));
    const newScene: Scene = {
      id: newId,
      title: `Scene ${doc!.scenes.length + 1}`,
      duration_s: 10,
      regions,
    };
    updateDoc((d) => ({ ...d, scenes: [...d.scenes, newScene] }));
    setActiveSceneId(newId);
    setSelectedRegionId(regions[0]?.id ?? null);
  }

  function duplicateScene() {
    const orig = activeScene;
    const newId = `scene-${Date.now()}`;
    const cloned: Scene = {
      id: newId,
      title: orig.title ? `${orig.title} (copy)` : undefined,
      duration_s: orig.duration_s,
      regions: orig.regions.map((r, i) => ({ ...r, id: `region-${Date.now()}-${i}` })),
    };
    updateDoc((d) => {
      const idx = d.scenes.findIndex((s) => s.id === orig.id);
      const next = [...d.scenes];
      next.splice(idx + 1, 0, cloned);
      return { ...d, scenes: next };
    });
    setActiveSceneId(newId);
    setSelectedRegionId(cloned.regions[0]?.id ?? null);
  }

  function deleteActiveScene() {
    if (doc!.scenes.length <= 1) {
      alert("Can't delete the last scene.");
      return;
    }
    if (!confirm(`Delete "${activeScene.title || 'Scene'}"?`)) return;
    const idx = doc!.scenes.findIndex((s) => s.id === activeScene.id);
    const remaining = doc!.scenes.filter((s) => s.id !== activeScene.id);
    updateDoc((d) => ({ ...d, scenes: remaining }));
    const fallback = remaining[Math.max(0, idx - 1)];
    setActiveSceneId(fallback.id);
    setSelectedRegionId(fallback.regions[0]?.id ?? null);
  }

  function moveScene(delta: -1 | 1) {
    const idx = doc!.scenes.findIndex((s) => s.id === activeScene.id);
    const target = idx + delta;
    if (target < 0 || target >= doc!.scenes.length) return;
    updateDoc((d) => {
      const next = [...d.scenes];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...d, scenes: next };
    });
  }

  function renameActiveScene() {
    const next = prompt('Scene title', activeScene.title || '');
    if (next === null) return;
    updateActiveScene((s) => ({ ...s, title: next || undefined }));
  }

  /* ------------ Layout ------------ */
  function updateLayout(layout: Layout) {
    // Apply to all scenes: reslot regions to new layout's slots.
    updateDoc((d) => {
      const newSlots = slotsForLayout(layout);
      const newScenes = d.scenes.map((sc) => {
        let newRegions: Region[];
        if (layout.kind === 'freeform') {
          newRegions = sc.regions.map((r, i) => ({
            ...r,
            slot: undefined,
            rect_pct: r.rect_pct ?? { x: 10 + i * 5, y: 10 + i * 5, w: 50, h: 30 },
          }));
        } else {
          newRegions = newSlots.map((slot, i) => {
            const old = sc.regions[i];
            if (old) return { ...old, slot, rect_pct: undefined };
            return {
              id: `region-${Date.now()}-${sc.id}-${i}`,
              slot,
              block: newBlock('text'),
            };
          });
        }
        return { ...sc, regions: newRegions };
      });
      return { ...d, layout, scenes: newScenes };
    });
  }

  /* ------------ Region ops on active scene ------------ */
  function updateRegionBlock(regionId: string, block: Block) {
    updateActiveScene((s) => ({
      ...s,
      regions: s.regions.map((r) => (r.id === regionId ? { ...r, block } : r)),
    }));
  }

  function addFreeformRegion(kind: BlockKind) {
    if (doc!.layout.kind !== 'freeform') return;
    const region: Region = {
      id: `region-${Date.now()}`,
      rect_pct: { x: 10, y: 10, w: 40, h: 30 },
      block: newBlock(kind),
    };
    updateActiveScene((s) => ({ ...s, regions: [...s.regions, region] }));
    setSelectedRegionId(region.id);
  }

  function removeRegion(regionId: string) {
    if (doc!.layout.kind !== 'freeform') return;
    updateActiveScene((s) => ({ ...s, regions: s.regions.filter((r) => r.id !== regionId) }));
    if (selectedRegionId === regionId) setSelectedRegionId(null);
  }

  /* ------------ Save / publish ------------ */
  async function save() {
    if (!id || !doc) return;
    setSaving(true); setErr(null);
    try {
      await packageEditor.saveDraft(id, doc);
      setDirty(false); setSavedAt(new Date());
    } catch { setErr('Save failed.'); }
    finally { setSaving(false); }
  }

  async function publish() {
    if (!id || !doc) return;
    if (dirty) {
      if (!confirm('You have unsaved changes. Save and publish?')) return;
      await save();
    } else if (!confirm('Publish a new version? This creates an immutable snapshot devices can run.')) {
      return;
    }
    setPublishing(true); setErr(null);
    try {
      const r = await packageEditor.publish(id);
      alert(`Published v${r.version.version} (${formatBytes(r.version.size_bytes)})`);
    } catch (e: unknown) {
      // Phase 3d.1 — surface storage_quota_exceeded via modal
      const quota = extractQuotaDetail(e);
      if (quota) {
        setQuotaDetail(quota);
      } else {
        const detail = ((e as { detail?: { error?: string } }).detail?.error);
        setErr(detail === 'missing_assets'
          ? 'Some referenced assets are missing. Replace them and try again.'
          : 'Publish failed.');
      }
    } finally { setPublishing(false); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 0px)' }}>
      {/* Phase 3d.1 — quota modal */}
      {quotaDetail && (
        <QuotaExceededModal detail={quotaDetail} onClose={() => setQuotaDetail(null)} />
      )}
      {/* Top toolbar */}
      <div className="toolbar" style={{ borderBottom: '1px solid var(--border)', marginBottom: 0, padding: '0.6em 1em' }}>
        <button onClick={() => navigate('/packages')}>← Packages</button>
        <h2 style={{ margin: 0, marginLeft: '0.6em' }}>{pkgName}</h2>
        <div style={{ marginLeft: '0.8em', color: 'var(--fg-3)', fontSize: '0.85em' }}>
          Layout:
          <select value={doc.layout.kind}
            onChange={(e) => {
              const k = e.target.value as LayoutKind;
              const next: Layout = k === 'split-h' ? { kind: 'split-h', ratio: [50, 50] }
                : k === 'split-v' ? { kind: 'split-v', ratio: [50, 50] }
                : k === 'pip' ? { kind: 'pip', position: 'br', size_pct: 25 }
                : k === 'ticker-bottom' ? { kind: 'ticker-bottom', ticker_h_pct: 12 }
                : { kind: k } as Layout;
              updateLayout(next);
            }}
            style={{ width: 'auto', marginLeft: '0.4em' }}>
            {LAYOUTS.map((l) => <option key={l.kind} value={l.kind}>{l.label}</option>)}
          </select>
        </div>
        <div className="spacer" />
        <div style={{ color: 'var(--fg-3)', fontSize: '0.82em', marginRight: '0.6em' }}>
          {dirty ? 'Unsaved changes' : savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : 'No changes'}
        </div>
        <button onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save draft'}
        </button>
        <button className="primary" onClick={publish} disabled={publishing}>
          {publishing ? 'Publishing…' : 'Publish'}
        </button>
      </div>

      {/* Scene strip */}
      <SceneStrip
        scenes={doc.scenes}
        activeId={activeScene.id}
        onSelect={(id) => {
          setActiveSceneId(id);
          const sc = doc.scenes.find((s) => s.id === id);
          setSelectedRegionId(sc?.regions[0]?.id ?? null);
        }}
        onAdd={addScene}
        onDuplicate={duplicateScene}
        onDelete={deleteActiveScene}
        onMove={moveScene}
        onRename={renameActiveScene}
      />

      {err && <div className="error" style={{ margin: '0.6em 1em' }}>{err}</div>}

      {/* 3-pane body */}
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr 340px', flex: 1, overflow: 'hidden' }}>
        {/* Left pane */}
        <div style={{ borderRight: '1px solid var(--border)', overflowY: 'auto', padding: '1em' }}>
          {doc.layout.kind === 'freeform' ? (
            <>
              <h3 style={{ marginTop: 0 }}>Add block</h3>
              <BlockTypeList
                onPick={(k) => {
                  if (!PHASE_SUPPORTED.includes(k)) {
                    if (!confirm(`${blockLabel(k)} renders as a placeholder on devices today. Add anyway?`)) return;
                  }
                  addFreeformRegion(k);
                }}
              />
              <h3 style={{ marginTop: '1.4em' }}>Regions in this scene</h3>
              {activeScene.regions.map((r) => (
                <RegionListItem key={r.id} region={r}
                  selected={r.id === selectedRegionId}
                  onSelect={() => setSelectedRegionId(r.id)} />
              ))}
            </>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>Slots in this scene</h3>
              <p style={{ fontSize: '0.85em', color: 'var(--fg-3)' }}>
                Click a slot to select it, then pick a block kind on the right.
              </p>
              {activeScene.regions.map((r) => (
                <RegionListItem key={r.id} region={r}
                  selected={r.id === selectedRegionId}
                  onSelect={() => setSelectedRegionId(r.id)} />
              ))}
            </>
          )}
          <h3 style={{ marginTop: '1.4em' }}>This scene</h3>
          <label style={{ fontSize: '0.85em', color: 'var(--fg-3)' }}>Duration (seconds)</label>
          <input type="number" min={1} value={activeScene.duration_s}
            onChange={(e) => updateActiveScene((s) => ({ ...s, duration_s: Number(e.target.value) }))} />
        </div>

        {/* Center: canvas */}
        <div style={{ background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <Canvas
            doc={doc}
            scene={activeScene}
            selectedRegionId={selectedRegionId}
            onSelectRegion={setSelectedRegionId}
            onRemoveRegion={removeRegion}
          />
        </div>

        {/* Right pane: properties */}
        <div style={{ borderLeft: '1px solid var(--border)', overflowY: 'auto', padding: '1em' }}>
          {selectedRegion ? (
            <BlockProperties
              region={selectedRegion}
              onBlockChange={(b) => updateRegionBlock(selectedRegion.id, b)}
              isFreeform={doc.layout.kind === 'freeform'}
              onRectChange={(rect) => updateActiveScene((s) => ({
                ...s,
                regions: s.regions.map((r) =>
                  r.id === selectedRegion.id ? { ...r, rect_pct: rect } : r),
              }))}
              onChangeBlockKind={(kind) => updateRegionBlock(selectedRegion.id, newBlock(kind))}
              onSetSceneDuration={(seconds) =>
                updateActiveScene((s) => ({ ...s, duration_s: seconds }))
              }
            />
          ) : (
            <div style={{ color: 'var(--fg-3)' }}>Select a region to edit its block.</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================ Scene strip */

function SceneStrip({
  scenes, activeId, onSelect, onAdd, onDuplicate, onDelete, onMove, onRename,
}: {
  scenes: Scene[];
  activeId: string;
  onSelect: (id: string) => void;
  onAdd: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onMove: (delta: -1 | 1) => void;
  onRename: () => void;
}) {
  const activeIdx = scenes.findIndex((s) => s.id === activeId);
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      borderBottom: '1px solid var(--border)',
      padding: '0.4em 1em', gap: '0.4em',
      overflowX: 'auto',
      background: 'var(--bg-1)',
    }}>
      <div style={{ color: 'var(--fg-3)', fontSize: '0.82em', marginRight: '0.4em' }}>Scenes:</div>
      {scenes.map((s, i) => (
        <button key={s.id}
          onClick={() => onSelect(s.id)}
          style={{
            padding: '0.35em 0.7em', fontSize: '0.85em', whiteSpace: 'nowrap',
            background: s.id === activeId ? 'var(--accent)' : 'var(--bg-2)',
            color: s.id === activeId ? '#fff' : 'var(--fg-1)',
            borderColor: s.id === activeId ? 'var(--accent)' : 'var(--border)',
          }}>
          {i + 1}. {s.title || 'Untitled'} <span style={{ opacity: 0.7, fontSize: '0.85em' }}>({s.duration_s}s)</span>
        </button>
      ))}
      <button onClick={onAdd} style={{ fontSize: '0.85em', padding: '0.35em 0.7em' }}>+ New scene</button>
      <div className="spacer" />
      <button onClick={onRename} title="Rename" style={{ fontSize: '0.82em' }}>Rename</button>
      <button onClick={() => onMove(-1)} disabled={activeIdx <= 0} title="Move left" style={{ fontSize: '0.82em' }}>←</button>
      <button onClick={() => onMove(1)} disabled={activeIdx >= scenes.length - 1} title="Move right" style={{ fontSize: '0.82em' }}>→</button>
      <button onClick={onDuplicate} style={{ fontSize: '0.82em' }}>Duplicate</button>
      <button className="danger" onClick={onDelete} disabled={scenes.length <= 1}
        style={{ fontSize: '0.82em' }}>Delete</button>
    </div>
  );
}

function RegionListItem({ region, selected, onSelect }: { region: Region; selected: boolean; onSelect: () => void }) {
  return (
    <div onClick={onSelect}
      style={{
        padding: '0.5em 0.7em', cursor: 'pointer', borderRadius: 4,
        marginBottom: '0.3em',
        background: selected ? 'var(--bg-2)' : 'transparent',
        borderLeft: selected ? '2px solid var(--accent)' : '2px solid transparent',
      }}>
      <div style={{ fontSize: '0.85em', fontWeight: 500 }}>{region.slot || region.id.slice(0, 8)}</div>
      <div style={{ fontSize: '0.72em', color: 'var(--fg-3)' }}>{blockLabel(region.block.kind)}</div>
    </div>
  );
}

/* ============================================================ Canvas */

function Canvas({
  doc, scene, selectedRegionId, onSelectRegion, onRemoveRegion,
}: {
  doc: PackageDocument;
  scene: Scene;
  selectedRegionId: string | null;
  onSelectRegion: (id: string) => void;
  onRemoveRegion: (id: string) => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    function fit() {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const parent = wrap.parentElement!;
      const padding = 32;
      const availW = parent.clientWidth - padding * 2;
      const availH = parent.clientHeight - padding * 2;
      const targetW = doc.meta.resolution.w;
      const targetH = doc.meta.resolution.h;
      const s = Math.min(availW / targetW, availH / targetH);
      setScale(s);
    }
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [doc.meta.resolution]);

  const slotMap = useMemo(() => computeSlotMap(doc.layout), [doc.layout]);

  return (
    <div ref={wrapRef} style={{
      width: doc.meta.resolution.w * scale,
      height: doc.meta.resolution.h * scale,
      position: 'relative',
      background: '#000',
      boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
    }}>
      <div style={{
        width: doc.meta.resolution.w, height: doc.meta.resolution.h,
        transform: `scale(${scale})`, transformOrigin: 'top left',
        position: 'relative',
        display: 'grid',
        ...slotMap.grid,
      }}>
        {scene.regions.map((r) => (
          <RegionPreview
            key={r.id}
            region={r}
            slotMap={slotMap}
            isFreeform={doc.layout.kind === 'freeform'}
            selected={r.id === selectedRegionId}
            onSelect={() => onSelectRegion(r.id)}
            onRemove={() => onRemoveRegion(r.id)}
          />
        ))}
      </div>
    </div>
  );
}

interface SlotMap {
  grid: React.CSSProperties;
  slots: Record<string, { gridArea?: string; absolute?: { left: string; top: string; right?: string; bottom?: string; width: string; height: string } }>;
  freeform: boolean;
}

function computeSlotMap(layout: Layout): SlotMap {
  switch (layout.kind) {
    case 'fullscreen':
      return { grid: { gridTemplateRows: '1fr', gridTemplateColumns: '1fr' },
        slots: { main: { gridArea: '1 / 1 / 2 / 2' } }, freeform: false };
    case 'split-h':
      return { grid: { gridTemplateColumns: `${layout.ratio[0]}fr ${layout.ratio[1]}fr` },
        slots: { left: { gridArea: '1 / 1 / 2 / 2' }, right: { gridArea: '1 / 2 / 2 / 3' } }, freeform: false };
    case 'split-v':
      return { grid: { gridTemplateRows: `${layout.ratio[0]}fr ${layout.ratio[1]}fr` },
        slots: { top: { gridArea: '1 / 1 / 2 / 2' }, bottom: { gridArea: '2 / 1 / 3 / 2' } }, freeform: false };
    case 'thirds-h':
      return { grid: { gridTemplateColumns: '1fr 1fr 1fr' },
        slots: { left: { gridArea: '1 / 1 / 2 / 2' }, middle: { gridArea: '1 / 2 / 2 / 3' }, right: { gridArea: '1 / 3 / 2 / 4' } },
        freeform: false };
    case 'thirds-v':
      return { grid: { gridTemplateRows: '1fr 1fr 1fr' },
        slots: { top: { gridArea: '1 / 1 / 2 / 2' }, middle: { gridArea: '2 / 1 / 3 / 2' }, bottom: { gridArea: '3 / 1 / 4 / 2' } },
        freeform: false };
    case 'ticker-bottom': {
      const th = layout.ticker_h_pct;
      return { grid: { gridTemplateRows: `${100 - th}% ${th}%` },
        slots: { main: { gridArea: '1 / 1 / 2 / 2' }, ticker: { gridArea: '2 / 1 / 3 / 2' } },
        freeform: false };
    }
    case 'pip': {
      const sz = layout.size_pct;
      const pos = layout.position;
      const slot: SlotMap['slots']['pip'] = {
        absolute: {
          left:   pos === 'tl' || pos === 'bl' ? '2%' : '',
          right:  pos === 'tr' || pos === 'br' ? '2%' : '',
          top:    pos === 'tl' || pos === 'tr' ? '2%' : '',
          bottom: pos === 'bl' || pos === 'br' ? '2%' : '',
          width: `${sz}%`, height: `${sz}%`,
        } as SlotMap['slots']['pip']['absolute'],
      };
      return { grid: {}, slots: { main: { gridArea: '1 / 1 / 2 / 2' }, pip: slot }, freeform: false };
    }
    case 'freeform':
      return { grid: {}, slots: {}, freeform: true };
  }
}

function RegionPreview({
  region, slotMap, isFreeform, selected, onSelect, onRemove,
}: {
  region: Region;
  slotMap: SlotMap;
  isFreeform: boolean;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) {
  const style: React.CSSProperties = {
    position: 'relative', overflow: 'hidden', cursor: 'pointer',
    outline: selected ? '3px solid var(--accent)' : 'none',
    outlineOffset: '-3px',
  };
  if (isFreeform && region.rect_pct) {
    style.position = 'absolute';
    style.left = `${region.rect_pct.x}%`;
    style.top = `${region.rect_pct.y}%`;
    style.width = `${region.rect_pct.w}%`;
    style.height = `${region.rect_pct.h}%`;
  } else if (region.slot && slotMap.slots[region.slot]) {
    const s = slotMap.slots[region.slot];
    if (s.gridArea) style.gridArea = s.gridArea;
    if (s.absolute) {
      style.position = 'absolute';
      if (s.absolute.left) style.left = s.absolute.left;
      if (s.absolute.right) style.right = s.absolute.right;
      if (s.absolute.top) style.top = s.absolute.top;
      if (s.absolute.bottom) style.bottom = s.absolute.bottom;
      style.width = s.absolute.width;
      style.height = s.absolute.height;
      style.zIndex = 10;
      style.boxShadow = '0 4px 30px rgba(0,0,0,0.5)';
    }
  }
  return (
    <div style={style} onClick={(e) => { e.stopPropagation(); onSelect(); }}>
      <BlockPreview block={region.block} />
      {isFreeform && selected && (
        <button className="danger"
          style={{ position: 'absolute', top: '0.4em', right: '0.4em', zIndex: 20, fontSize: '0.78em' }}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}>×</button>
      )}
    </div>
  );
}

function BlockPreview({ block }: { block: Block }) {
  switch (block.kind) {
    case 'text': {
      const isMarquee = block.variant === 'marquee';
      const style: React.CSSProperties = {
        width: '100%', height: '100%',
        background: block.background ?? '#111827',
        color: block.color ?? '#fff',
        fontSize: block.font_size_px ? `${block.font_size_px}px` : '48px',
        fontWeight: block.font_weight ?? 400,
        padding: block.padding_px ? `${block.padding_px}px` : '0',
        display: 'flex',
        alignItems: block.vertical_align === 'top' ? 'flex-start'
          : block.vertical_align === 'bottom' ? 'flex-end' : 'center',
        justifyContent: block.align === 'left' ? 'flex-start'
          : block.align === 'right' ? 'flex-end' : 'center',
        whiteSpace: isMarquee ? 'nowrap' : 'pre-wrap',
        wordBreak: 'break-word', overflow: 'hidden',
      };
      return <div style={style}>{block.text || '(empty)'}</div>;
    }
    case 'image':
      if (!block.asset_id) return <PlaceholderPreview label="No image selected" tone="warn" />;
      return (
        <div style={{
          width: '100%', height: '100%', background: block.background ?? '#000',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img src={assets.rawUrl(block.asset_id)} alt=""
            style={{
              maxWidth: '100%', maxHeight: '100%',
              width: block.fit === 'fill' ? '100%' : block.fit === 'cover' ? '100%' : 'auto',
              height: block.fit === 'fill' ? '100%' : block.fit === 'cover' ? '100%' : 'auto',
              objectFit: block.fit ?? 'contain',
            }} />
        </div>
      );
    case 'video':
      if (!block.asset_id) return <PlaceholderPreview label="No video selected" tone="warn" />;
      return (
        <div style={{
          width: '100%', height: '100%', background: '#000',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <video src={assets.rawUrl(block.asset_id)}
            muted loop autoPlay playsInline
            style={{
              maxWidth: '100%', maxHeight: '100%',
              width: block.fit === 'fill' ? '100%' : block.fit === 'cover' ? '100%' : 'auto',
              height: block.fit === 'fill' ? '100%' : block.fit === 'cover' ? '100%' : 'auto',
              objectFit: block.fit ?? 'contain',
            }} />
        </div>
      );
    case 'web':
      if (!block.url) return <PlaceholderPreview label="No URL set" tone="warn" />;
      return (
        <div style={{ width: '100%', height: '100%', background: '#fff', position: 'relative' }}>
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#666', fontFamily: 'monospace', fontSize: 14,
            background: '#1f2937', textAlign: 'center', padding: '1em',
          }}>
            <div>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🌐</div>
              <div style={{ color: '#cbd5e1' }}>{block.url}</div>
              <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 6 }}>Web preview disabled in editor</div>
            </div>
          </div>
        </div>
      );
    case 'youtube':
      if (!block.video_id) return <PlaceholderPreview label="No YouTube video set" tone="warn" />;
      return (
        <div style={{
          width: '100%', height: '100%', background: '#000', position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img alt="" src={`https://img.youtube.com/vi/${block.video_id}/hqdefault.jpg`}
            style={{ maxWidth: '100%', maxHeight: '100%' }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <div style={{ position: 'absolute', top: 8, left: 8, color: '#fff',
            fontSize: 12, background: 'rgba(0,0,0,0.5)', padding: '3px 6px', borderRadius: 3 }}>
            ▶ YouTube · {block.is_playlist ? 'playlist' : block.video_id}
          </div>
        </div>
      );
    case 'weather':
      return (
        <div style={{
          width: '100%', height: '100%',
          background: 'linear-gradient(180deg, #0f1f3a 0%, #0b1220 100%)',
          color: '#fff', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', padding: '1em',
        }}>
          <div style={{ fontSize: '0.85em', opacity: 0.75 }}>{block.location || '(no location)'}</div>
          <div style={{ fontSize: 56, marginTop: 8 }}>⛅</div>
          <div style={{ fontSize: 36, fontWeight: 600 }}>--°</div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
            Live data fetched on the Pi · refreshes every 10 min
          </div>
        </div>
      );
    case 'clock': {
      if (block.style === 'analog') return <AnalogClockPreview block={block} />;
      const now = new Date();
      const time = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
      const date = now.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      return (
        <div style={{
          width: '100%', height: '100%',
          background: block.background ?? '#0b1220',
          color: block.color ?? '#fff',
          fontSize: block.font_size_px ? `${block.font_size_px}px` : '96px',
          display: 'flex', flexDirection: 'column',
          alignItems: block.align === 'left' ? 'flex-start' : block.align === 'right' ? 'flex-end' : 'center',
          justifyContent: 'center',
          fontVariantNumeric: 'tabular-nums', fontWeight: 600,
        }}>
          <div>{time}</div>
          {block.show_date && (
            <div style={{ fontSize: '0.35em', opacity: 0.85, marginTop: '0.3em', fontWeight: 400 }}>{date}</div>
          )}
        </div>
      );
    }
    case 'pdf':
      if (!block.asset_id) return <PlaceholderPreview label="No PDF selected" tone="warn" />;
      return (
        <div style={{
          width: '100%', height: '100%', background: '#fff',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          color: '#374151', position: 'relative', overflow: 'hidden',
        }}>
          {/* Stack-of-pages illustration */}
          <div style={{ position: 'relative', width: 110, height: 140 }}>
            {[10, 5, 0].map((off, i) => (
              <div key={i} style={{
                position: 'absolute', top: off, left: off,
                width: 90, height: 120, background: '#fff', border: '1px solid #d1d5db',
                boxShadow: '0 2px 6px rgba(0,0,0,0.12)', borderRadius: 4,
                padding: 8, fontSize: 6, color: '#9ca3af', overflow: 'hidden',
              }}>
                {Array.from({ length: 11 }).map((_, k) => (
                  <div key={k} style={{ height: 1.5, background: '#e5e7eb', margin: '4px 0', width: k === 10 ? '60%' : '100%' }} />
                ))}
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, marginTop: 18, fontFamily: 'monospace' }}>PDF</div>
          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>
            {block.page_duration_s ?? 5}s per page · rasterised at publish
          </div>
        </div>
      );
    case 'rss':
      if (!block.url) return <PlaceholderPreview label="No RSS feed URL" tone="warn" />;
      return block.style === 'ticker' ? (
        <div style={{
          width: '100%', height: '100%', background: '#0b1220', color: '#f1f5f9',
          display: 'flex', alignItems: 'center', overflow: 'hidden',
          fontFamily: 'system-ui, sans-serif', fontSize: '1.2em', position: 'relative',
        }}>
          <div style={{ whiteSpace: 'nowrap', padding: '0 1em', opacity: 0.85 }}>
            📡 Latest headline · Another headline · One more headline · And another…
          </div>
          <div style={{ position: 'absolute', top: 6, left: 8, fontSize: 10, color: '#60a5fa',
            fontFamily: 'monospace', background: 'rgba(0,0,0,0.4)', padding: '2px 5px', borderRadius: 3 }}>
            RSS · ticker
          </div>
        </div>
      ) : (
        <div style={{
          width: '100%', height: '100%', background: '#0b1220', color: '#e2e8f0',
          padding: '1.5em', fontFamily: 'system-ui, sans-serif',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <div style={{ fontSize: 10, color: '#60a5fa', fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {(() => { try { return new URL(block.url).hostname; } catch { return 'RSS feed'; } })()}
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.25, margin: '8px 0' }}>
            Headlines fetched at publish time
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>
            Up to {block.item_count ?? 10} items · rotates every {block.rotate_ms ?? 6000}ms
          </div>
        </div>
      );
    case 'custom-html':
      return (
        <div style={{ width: '100%', height: '100%', position: 'relative', background: '#000' }}>
          <iframe
            sandbox="allow-scripts"
            srcDoc={buildCustomHtmlSrcdoc(block.html || '', block.css, block.js)}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, background: 'transparent' }} />
          <div style={{ position: 'absolute', top: 6, left: 8, fontSize: 10, color: '#fbbf24',
            fontFamily: 'monospace', background: 'rgba(0,0,0,0.6)', padding: '2px 5px',
            borderRadius: 3, pointerEvents: 'none' }}>
            Custom HTML · sandboxed
          </div>
        </div>
      );
    default:
      return <PlaceholderPreview label={`[ ${(block as { kind: string }).kind} — not yet supported in editor ]`} />;
  }
}

/** Build an iframe srcdoc string mirroring what the renderer produces on the Pi. */
function buildCustomHtmlSrcdoc(html: string, css?: string, js?: string): string {
  const safeCss = String(css ?? '').replace(/<(\/?)style\b/gi, (_, slash) => '\u003c' + slash + 'style');
  const safeJs  = String(js  ?? '').replace(/<(\/?)script\b/gi, (_, slash) => '\u003c' + slash + 'script');
  return (
    '<!DOCTYPE html><html><head>' +
    '<meta charset="utf-8">' +
    '<style>html,body{margin:0;padding:0;height:100%;background:transparent;overflow:hidden}' +
    (safeCss || '') +
    '</style></head><body>' + html +
    (safeJs ? `<script>(function(){try{${safeJs}}catch(e){var p=document.createElement('pre');p.textContent=String(e);p.style.cssText='color:#f87171;background:rgba(0,0,0,0.6);padding:0.5em;margin:0';document.body.appendChild(p);}})();<\/script>` : '') +
    '</body></html>'
  );
}

function AnalogClockPreview({ block }: { block: ClockBlock }) {
  const now = new Date();
  const s = now.getSeconds(), m = now.getMinutes(), h = now.getHours() % 12;
  const sDeg = s * 6;
  const mDeg = m * 6 + s * 0.1;
  const hDeg = h * 30 + m * 0.5;
  const col = block.color || '#fff';
  return (
    <div style={{
      width: '100%', height: '100%', background: block.background ?? '#0b1220',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg viewBox="-50 -50 100 100" preserveAspectRatio="xMidYMid meet"
        style={{ width: '90%', height: '90%' }}>
        <circle r="46" fill="none" stroke={col} strokeWidth="2" />
        {Array.from({ length: 12 }, (_, i) => {
          const a = (i / 12) * Math.PI * 2;
          return (
            <line key={i}
              x1={Math.sin(a) * 40} y1={-Math.cos(a) * 40}
              x2={Math.sin(a) * 44} y2={-Math.cos(a) * 44}
              stroke={col} strokeWidth={i % 3 === 0 ? 2.5 : 1} strokeLinecap="round" />
          );
        })}
        <line x1="0" y1="0" x2="0" y2="-22" stroke={col} strokeWidth="3.5" strokeLinecap="round" transform={`rotate(${hDeg})`} />
        <line x1="0" y1="0" x2="0" y2="-34" stroke={col} strokeWidth="2.5" strokeLinecap="round" transform={`rotate(${mDeg})`} />
        <line x1="0" y1="0" x2="0" y2="-38" stroke="#f87171" strokeWidth="1" strokeLinecap="round" transform={`rotate(${sDeg})`} />
        <circle r="2" fill={col} />
      </svg>
    </div>
  );
}

function PlaceholderPreview({ label, tone }: { label: string; tone?: 'warn' }) {
  return (
    <div style={{
      width: '100%', height: '100%',
      background: tone === 'warn' ? '#3b2a16' : '#1f2937',
      color: tone === 'warn' ? '#fbbf24' : '#93a4bf',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'monospace', fontSize: '20px', textAlign: 'center', padding: '1em',
    }}>{label}</div>
  );
}

/* ============================================================ Block type picker */

function BlockTypeList({ onPick }: { onPick: (k: BlockKind) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3em' }}>
      {BLOCK_KINDS.map((k) => (
        <button key={k} onClick={() => onPick(k)}
          style={{ textAlign: 'left', padding: '0.5em 0.8em' }}>
          {blockLabel(k)}
          {!PHASE_SUPPORTED.includes(k) && (
            <span style={{ marginLeft: '0.4em', fontSize: '0.72em', color: 'var(--fg-3)' }}>· P2b</span>
          )}
        </button>
      ))}
    </div>
  );
}

/* ============================================================ Block properties */

function BlockProperties({
  region, onBlockChange, isFreeform, onRectChange, onChangeBlockKind, onSetSceneDuration,
}: {
  region: Region;
  onBlockChange: (b: Block) => void;
  isFreeform: boolean;
  onRectChange: (rect: { x: number; y: number; w: number; h: number }) => void;
  onChangeBlockKind: (k: BlockKind) => void;
  onSetSceneDuration: (seconds: number) => void;
}) {
  const b = region.block;
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Properties</h3>

      {isFreeform && region.rect_pct && (
        <fieldset style={{ border: '1px solid var(--border)', borderRadius: 4, padding: '0.6em', marginBottom: '0.8em' }}>
          <legend style={{ padding: '0 0.4em', fontSize: '0.85em', color: 'var(--fg-3)' }}>Position (%)</legend>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4em' }}>
            <label>X<input type="number" min={0} max={100} value={region.rect_pct.x}
              onChange={(e) => onRectChange({ ...region.rect_pct!, x: Number(e.target.value) })} /></label>
            <label>Y<input type="number" min={0} max={100} value={region.rect_pct.y}
              onChange={(e) => onRectChange({ ...region.rect_pct!, y: Number(e.target.value) })} /></label>
            <label>W<input type="number" min={1} max={100} value={region.rect_pct.w}
              onChange={(e) => onRectChange({ ...region.rect_pct!, w: Number(e.target.value) })} /></label>
            <label>H<input type="number" min={1} max={100} value={region.rect_pct.h}
              onChange={(e) => onRectChange({ ...region.rect_pct!, h: Number(e.target.value) })} /></label>
          </div>
        </fieldset>
      )}

      <label>Block kind</label>
      <select value={b.kind} onChange={(e) => onChangeBlockKind(e.target.value as BlockKind)}>
        {BLOCK_KINDS.map((k) => <option key={k} value={k}>{blockLabel(k)}</option>)}
      </select>

      <div style={{ marginTop: '1em' }}>
        {b.kind === 'text' && <TextProps block={b} onChange={onBlockChange} />}
        {b.kind === 'image' && <ImageProps block={b} onChange={onBlockChange} />}
        {b.kind === 'video' && <VideoProps block={b} onChange={onBlockChange}
          onDurationDetected={onSetSceneDuration} />}
        {b.kind === 'web' && <WebProps block={b} onChange={onBlockChange} />}
        {b.kind === 'clock' && <ClockProps block={b} onChange={onBlockChange} />}
        {b.kind === 'youtube' && <YouTubeProps block={b} onChange={onBlockChange} />}
        {b.kind === 'weather' && <WeatherProps block={b} onChange={onBlockChange} />}
        {b.kind === 'pdf' && <PdfProps block={b} onChange={onBlockChange} />}
        {b.kind === 'rss' && <RssProps block={b} onChange={onBlockChange} />}
        {b.kind === 'custom-html' && <CustomHtmlProps block={b} onChange={onBlockChange} />}
        {!PHASE_SUPPORTED.includes(b.kind) && (
          <div className="card" style={{ background: 'rgba(245,158,11,0.1)', borderColor: 'var(--warn)' }}>
            <strong style={{ color: 'var(--warn)' }}>Unsupported block</strong>
            <div style={{ fontSize: '0.85em', marginTop: '0.3em' }}>
              {blockLabel(b.kind)} is recognised by the schema but the renderer doesn&apos;t handle it yet.
              Configure freely; data is persisted in the document.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ----- Property panels ----- */

function TextProps({ block, onChange }: { block: TextBlock; onChange: (b: TextBlock) => void }) {
  return (
    <div>
      <label>Text<textarea rows={4} value={block.text}
        onChange={(e) => onChange({ ...block, text: e.target.value })} /></label>
      <label>Variant
        <select value={block.variant ?? 'body'}
          onChange={(e) => onChange({ ...block, variant: e.target.value as TextBlock['variant'] })}>
          <option value="body">Body</option>
          <option value="heading">Heading</option>
          <option value="marquee">Marquee (scrolling)</option>
        </select>
      </label>
      <label>Font size (px)<input type="number" min={8} max={400} value={block.font_size_px ?? 48}
        onChange={(e) => onChange({ ...block, font_size_px: Number(e.target.value) })} /></label>
      <label>Font weight
        <select value={block.font_weight ?? 400}
          onChange={(e) => onChange({ ...block, font_weight: Number(e.target.value) as TextBlock['font_weight'] })}>
          {[400, 500, 600, 700, 800].map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
      </label>
      <label>Text color<input type="color" value={block.color ?? '#ffffff'}
        onChange={(e) => onChange({ ...block, color: e.target.value })} /></label>
      <label>Background<input type="color" value={block.background ?? '#111827'}
        onChange={(e) => onChange({ ...block, background: e.target.value })} /></label>
      <label>Horizontal align
        <select value={block.align ?? 'center'}
          onChange={(e) => onChange({ ...block, align: e.target.value as TextBlock['align'] })}>
          <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
        </select>
      </label>
      <label>Vertical align
        <select value={block.vertical_align ?? 'middle'}
          onChange={(e) => onChange({ ...block, vertical_align: e.target.value as TextBlock['vertical_align'] })}>
          <option value="top">Top</option><option value="middle">Middle</option><option value="bottom">Bottom</option>
        </select>
      </label>
      {block.variant === 'marquee' && (
        <label>Marquee speed (px/sec)<input type="number" min={20} max={600} value={block.marquee_speed_pps ?? 120}
          onChange={(e) => onChange({ ...block, marquee_speed_pps: Number(e.target.value) })} /></label>
      )}
    </div>
  );
}

function ImageProps({ block, onChange }: { block: ImageBlock; onChange: (b: ImageBlock) => void }) {
  const [showPicker, setShowPicker] = useState(false);
  return (
    <div>
      <label>Image asset</label>
      <div style={{ display: 'flex', gap: '0.4em', alignItems: 'center', marginBottom: '0.6em' }}>
        {block.asset_id ? (
          <img src={assets.rawUrl(block.asset_id)} alt=""
            style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4, background: 'var(--bg-2)' }} />
        ) : (
          <div style={{ width: 60, height: 60, borderRadius: 4, background: 'var(--bg-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-3)' }}>—</div>
        )}
        <button onClick={() => setShowPicker(true)} style={{ flex: 1 }}>
          {block.asset_id ? 'Change' : 'Pick image'}
        </button>
      </div>
      <label>Fit
        <select value={block.fit ?? 'contain'}
          onChange={(e) => onChange({ ...block, fit: e.target.value as ImageBlock['fit'] })}>
          <option value="contain">Contain (fit inside)</option>
          <option value="cover">Cover (fill, may crop)</option>
          <option value="fill">Fill (stretch)</option>
        </select>
      </label>
      <label>Background<input type="color" value={block.background ?? '#000000'}
        onChange={(e) => onChange({ ...block, background: e.target.value })} /></label>
      {showPicker && (
        <AssetPicker kind="image" onClose={() => setShowPicker(false)}
          onPick={(a) => onChange({ ...block, asset_id: a.id })} />
      )}
    </div>
  );
}

/** Probe a video's duration by loading metadata in a hidden element. */
function probeVideoDuration(url: string, timeoutMs = 15000): Promise<number | null> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    let done = false;
    const finish = (d: number | null) => {
      if (done) return; done = true;
      v.removeAttribute('src');
      try { v.load(); } catch { /* */ }
      resolve(d);
    };
    v.addEventListener('loadedmetadata', () => {
      const d = Number.isFinite(v.duration) && v.duration > 0 ? Math.ceil(v.duration) : null;
      finish(d);
    });
    v.addEventListener('error', () => finish(null));
    setTimeout(() => finish(null), timeoutMs);
    v.src = url;
  });
}

function VideoProps({
  block, onChange, onDurationDetected,
}: {
  block: VideoBlock;
  onChange: (b: VideoBlock) => void;
  onDurationDetected: (seconds: number) => void;
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [detected, setDetected] = useState<number | null>(null);
  const [probing, setProbing] = useState(false);
  const lastProbed = useRef<string>('');

  // When asset_id changes (or first mount with one set), probe duration and
  // auto-update the scene duration. Records the asset_id so we don't re-probe.
  useEffect(() => {
    if (!block.asset_id || lastProbed.current === block.asset_id) {
      setDetected(null);
      return;
    }
    lastProbed.current = block.asset_id;
    setProbing(true);
    setDetected(null);
    probeVideoDuration(assets.rawUrl(block.asset_id))
      .then((d) => {
        setProbing(false);
        if (d != null && d > 0) {
          setDetected(d);
          onDurationDetected(d);
        }
      });
  }, [block.asset_id, onDurationDetected]);

  return (
    <div>
      <label>Video asset</label>
      <div style={{ display: 'flex', gap: '0.4em', alignItems: 'center', marginBottom: '0.6em' }}>
        <div style={{
          width: 60, height: 60, borderRadius: 4, background: '#000', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4em',
        }}>{block.asset_id ? '▶' : '—'}</div>
        <button onClick={() => setShowPicker(true)} style={{ flex: 1 }}>
          {block.asset_id ? 'Change' : 'Pick video'}
        </button>
      </div>
      {probing && (
        <div style={{ fontSize: '0.82em', color: 'var(--fg-3)', marginBottom: '0.6em' }}>
          Detecting video duration…
        </div>
      )}
      {detected != null && (
        <div style={{
          background: 'rgba(16,185,129,0.1)', color: '#10b981',
          padding: '0.5em 0.8em', borderRadius: 4, fontSize: '0.85em',
          marginBottom: '0.6em', borderLeft: '3px solid #10b981',
        }}>
          ✓ Duration: {detected}s — scene duration set to match
        </div>
      )}
      <label>Fit
        <select value={block.fit ?? 'contain'}
          onChange={(e) => onChange({ ...block, fit: e.target.value as VideoBlock['fit'] })}>
          <option value="contain">Contain</option>
          <option value="cover">Cover</option>
          <option value="fill">Fill</option>
        </select>
      </label>
      <label><input type="checkbox" style={{ width: 'auto', marginRight: '0.4em' }}
        checked={block.loop !== false}
        onChange={(e) => onChange({ ...block, loop: e.target.checked })} />Loop</label>
      <label><input type="checkbox" style={{ width: 'auto', marginRight: '0.4em' }}
        checked={block.muted !== false}
        onChange={(e) => onChange({ ...block, muted: e.target.checked })} />Muted (required for autoplay)</label>
      {block.muted === false && (
        <div className="card" style={{ background: 'rgba(245,158,11,0.1)', borderColor: 'var(--warn)', marginTop: '0.4em' }}>
          <div style={{ fontSize: '0.82em', color: 'var(--warn)' }}>
            Unmuted videos may not autoplay on some devices.
          </div>
        </div>
      )}
      {showPicker && (
        <AssetPicker kind="video" onClose={() => setShowPicker(false)}
          onPick={(a) => onChange({ ...block, asset_id: a.id })} />
      )}
    </div>
  );
}

function WebProps({ block, onChange }: { block: WebBlock; onChange: (b: WebBlock) => void }) {
  return (
    <div>
      <label>URL<input type="url" placeholder="https://…" value={block.url}
        onChange={(e) => onChange({ ...block, url: e.target.value })} /></label>
      <label>Auto-refresh (seconds, 0 = off)
        <input type="number" min={0} value={block.refresh_s ?? 0}
          onChange={(e) => onChange({ ...block, refresh_s: Number(e.target.value) || undefined })} />
      </label>
      <div className="card" style={{ fontSize: '0.82em', color: 'var(--fg-3)' }}>
        The page is rendered in a sandboxed iframe on the Pi. Sites that send
        <code> X-Frame-Options: DENY</code> or restrictive CSP will refuse to load — host them yourself if needed.
      </div>
    </div>
  );
}

function ClockProps({ block, onChange }: { block: ClockBlock; onChange: (b: ClockBlock) => void }) {
  return (
    <div>
      <label>Style
        <select value={block.style ?? 'digital'}
          onChange={(e) => onChange({ ...block, style: e.target.value as ClockBlock['style'] })}>
          <option value="digital">Digital</option>
          <option value="analog">Analog</option>
        </select>
      </label>
      {block.style !== 'analog' && (
        <label><input type="checkbox" style={{ width: 'auto', marginRight: '0.4em' }}
          checked={!!block.show_date}
          onChange={(e) => onChange({ ...block, show_date: e.target.checked })} />Show date</label>
      )}
      <label>Timezone (IANA, optional)
        <input type="text" placeholder="Africa/Windhoek" value={block.timezone ?? ''}
          onChange={(e) => onChange({ ...block, timezone: e.target.value || undefined })} />
      </label>
      {block.style !== 'analog' && (
        <label>Font size (px)<input type="number" min={20} max={400} value={block.font_size_px ?? 96}
          onChange={(e) => onChange({ ...block, font_size_px: Number(e.target.value) })} /></label>
      )}
      <label>Color<input type="color" value={block.color ?? '#ffffff'}
        onChange={(e) => onChange({ ...block, color: e.target.value })} /></label>
      <label>Background<input type="color" value={block.background ?? '#0b1220'}
        onChange={(e) => onChange({ ...block, background: e.target.value })} /></label>
      {block.style !== 'analog' && (
        <label>Align
          <select value={block.align ?? 'center'}
            onChange={(e) => onChange({ ...block, align: e.target.value as ClockBlock['align'] })}>
            <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
          </select>
        </label>
      )}
    </div>
  );
}

function parseYouTubeInput(s: string): { video_id: string; is_playlist: boolean } | null {
  if (!s) return null;
  const trimmed = s.trim();
  // Playlist URL (must check before video, e.g. youtube.com/playlist?list=...)
  if (/youtube\.com\/playlist\?/.test(trimmed) || (/[?&]list=/.test(trimmed) && !/watch\?v=/.test(trimmed))) {
    const m = trimmed.match(/[?&]list=([A-Za-z0-9_-]+)/);
    if (m) return { video_id: m[1], is_playlist: true };
  }
  // Video patterns
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
    /^([A-Za-z0-9_-]{11})$/,
  ];
  for (const p of patterns) {
    const m = trimmed.match(p);
    if (m) return { video_id: m[1], is_playlist: false };
  }
  return null;
}

function YouTubeProps({ block, onChange }: { block: YouTubeBlock; onChange: (b: YouTubeBlock) => void }) {
  const [input, setInput] = useState(block.video_id);
  useEffect(() => { setInput(block.video_id); }, [block.video_id]);
  function apply(s: string) {
    setInput(s);
    const parsed = parseYouTubeInput(s);
    if (parsed) {
      onChange({ ...block, video_id: parsed.video_id, is_playlist: parsed.is_playlist });
    } else {
      onChange({ ...block, video_id: s, is_playlist: block.is_playlist });
    }
  }
  const embedUrl = block.video_id
    ? (block.is_playlist
        ? `https://www.youtube.com/embed/videoseries?list=${encodeURIComponent(block.video_id)}`
        : `https://www.youtube.com/embed/${encodeURIComponent(block.video_id)}`)
    : '';
  return (
    <div>
      <label>Video ID or URL
        <input type="text" placeholder="dQw4w9WgXcQ or full URL" value={input}
          onChange={(e) => apply(e.target.value)} />
      </label>
      <div style={{ fontSize: '0.8em', color: 'var(--fg-3)', marginBottom: '0.6em' }}>
        Accepts: raw 11-char ID, youtu.be links, watch URLs, embed URLs, shorts URLs, playlist URLs.
      </div>
      {embedUrl && (
        <div style={{ marginBottom: '0.6em' }}>
          <a href={embedUrl} target="_blank" rel="noopener"
             style={{ fontSize: '0.85em' }}>
            ↗ Open embed URL in browser
          </a>
          <div style={{ fontSize: '0.78em', color: 'var(--fg-3)', marginTop: '0.2em' }}>
            If it plays here but not on the Pi, the device is region-restricted.
            If it fails here too, the video has embedding disabled — try a different one
            (e.g. <code>dQw4w9WgXcQ</code> is always embeddable).
          </div>
        </div>
      )}
      <label><input type="checkbox" style={{ width: 'auto', marginRight: '0.4em' }}
        checked={!!block.is_playlist}
        onChange={(e) => onChange({ ...block, is_playlist: e.target.checked })} />This is a playlist</label>
      <label><input type="checkbox" style={{ width: 'auto', marginRight: '0.4em' }}
        checked={block.autoplay !== false}
        onChange={(e) => onChange({ ...block, autoplay: e.target.checked })} />Autoplay</label>
      <label><input type="checkbox" style={{ width: 'auto', marginRight: '0.4em' }}
        checked={block.mute !== false}
        onChange={(e) => onChange({ ...block, mute: e.target.checked })} />Mute (required for autoplay)</label>
      <label><input type="checkbox" style={{ width: 'auto', marginRight: '0.4em' }}
        checked={!!block.loop}
        onChange={(e) => onChange({ ...block, loop: e.target.checked })} />Loop</label>
    </div>
  );
}

function WeatherProps({ block, onChange }: { block: WeatherBlock; onChange: (b: WeatherBlock) => void }) {
  return (
    <div>
      <label>Location
        <input type="text" placeholder="Walvis Bay  or  -22.95,14.51" value={block.location}
          onChange={(e) => onChange({ ...block, location: e.target.value })} />
      </label>
      <div style={{ fontSize: '0.8em', color: 'var(--fg-3)', marginBottom: '0.6em' }}>
        Just the city name (no country code), or <code>lat,lng</code>. If a name returns no match,
        the renderer auto-retries with country suffix stripped.
      </div>
      <label>Units
        <select value={block.units ?? 'metric'}
          onChange={(e) => onChange({ ...block, units: e.target.value as WeatherBlock['units'] })}>
          <option value="metric">Metric (°C, km/h)</option>
          <option value="imperial">Imperial (°F, mph)</option>
        </select>
      </label>
      <label>Size (base font px, 0 = auto-fit to container)
        <input type="number" min={0} max={400} step={4}
          value={block.font_size_px ?? 0}
          onChange={(e) => {
            const n = Number(e.target.value);
            onChange({ ...block, font_size_px: n > 0 ? n : undefined });
          }} />
      </label>
      <div style={{ fontSize: '0.8em', color: 'var(--fg-3)', marginBottom: '0.6em' }}>
        At 0 the renderer scales the card to fill its region. Set a specific value to lock the size.
        Temperature renders at this size; everything else scales relative to it.
      </div>
      <div className="card" style={{ fontSize: '0.82em', color: 'var(--fg-3)' }}>
        Provided by <a href="https://open-meteo.com/" target="_blank" rel="noopener">open-meteo.com</a> — free, no API key. Pi must be online.
      </div>
    </div>
  );
}

function PdfProps({ block, onChange }: { block: PdfBlock; onChange: (b: PdfBlock) => void }) {
  const [showPicker, setShowPicker] = useState(false);
  return (
    <div>
      <label>PDF asset</label>
      <div style={{ display: 'flex', gap: '0.4em', alignItems: 'center', marginBottom: '0.6em' }}>
        <div style={{
          width: 60, height: 60, borderRadius: 4, background: '#fff', color: '#000',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2em', fontWeight: 700,
        }}>{block.asset_id ? 'PDF' : '—'}</div>
        <button onClick={() => setShowPicker(true)} style={{ flex: 1 }}>
          {block.asset_id ? 'Change PDF' : 'Pick PDF'}
        </button>
      </div>
      <label>Seconds per page
        <input type="number" min={1} max={3600} value={block.page_duration_s ?? 5}
          onChange={(e) => onChange({ ...block, page_duration_s: Number(e.target.value) })} />
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4em' }}>
        <label>First page<input type="number" min={1} value={block.first_page ?? 1}
          onChange={(e) => onChange({ ...block, first_page: Number(e.target.value) || 1 })} /></label>
        <label>Last page (0 = all)<input type="number" min={0} value={block.last_page ?? 0}
          onChange={(e) => onChange({ ...block, last_page: Number(e.target.value) || undefined })} /></label>
      </div>
      <div className="card" style={{ background: 'rgba(59,130,246,0.08)', borderColor: '#3b82f6',
        fontSize: '0.82em', color: 'var(--fg-3)', marginTop: '0.6em' }}>
        Pages are rasterised to PNG at publish time. Requires <code>poppler-utils</code> on the server
        (<code>sudo apt install -y poppler-utils</code>).
      </div>
      {showPicker && (
        <AssetPicker kind="pdf" onClose={() => setShowPicker(false)}
          onPick={(a) => onChange({ ...block, asset_id: a.id })} />
      )}
    </div>
  );
}

function RssProps({ block, onChange }: { block: RssBlock; onChange: (b: RssBlock) => void }) {
  return (
    <div>
      <label>Feed URL
        <input type="url" placeholder="https://example.com/feed.xml" value={block.url}
          onChange={(e) => onChange({ ...block, url: e.target.value })} />
      </label>
      <div style={{ fontSize: '0.8em', color: 'var(--fg-3)', marginBottom: '0.6em' }}>
        RSS 2.0 or Atom 1.0 feeds. Items are fetched and snapshotted into the bundle at publish.
        Republish the package to refresh content.
      </div>
      <label>Items to keep
        <input type="number" min={1} max={50} value={block.item_count ?? 10}
          onChange={(e) => onChange({ ...block, item_count: Number(e.target.value) })} />
      </label>
      <label>Display style
        <select value={(block as RssBlock & { style?: string }).style ?? 'card'}
          onChange={(e) => onChange({ ...block, style: e.target.value } as RssBlock & { style: string })}>
          <option value="card">Card (one item at a time, large)</option>
          <option value="ticker">Ticker (scrolling headline strip)</option>
        </select>
      </label>
      <label>Rotation interval (ms, card style only)
        <input type="number" min={1500} max={60000} step={500} value={block.rotate_ms ?? 6000}
          onChange={(e) => onChange({ ...block, rotate_ms: Number(e.target.value) })} />
      </label>
    </div>
  );
}

function CustomHtmlProps({ block, onChange }: { block: CustomHtmlBlock; onChange: (b: CustomHtmlBlock) => void }) {
  return (
    <div>
      <label>HTML
        <textarea value={block.html} rows={6} spellCheck={false}
          placeholder="<h1>Hello</h1>"
          style={{ fontFamily: 'monospace', fontSize: '0.85em', width: '100%' }}
          onChange={(e) => onChange({ ...block, html: e.target.value })} />
      </label>
      <label>CSS (optional)
        <textarea value={block.css ?? ''} rows={4} spellCheck={false}
          placeholder="body { color: white; font-family: sans-serif; }"
          style={{ fontFamily: 'monospace', fontSize: '0.85em', width: '100%' }}
          onChange={(e) => onChange({ ...block, css: e.target.value || undefined })} />
      </label>
      <label>JavaScript (optional)
        <textarea value={block.js ?? ''} rows={4} spellCheck={false}
          placeholder="document.body.style.background = 'navy';"
          style={{ fontFamily: 'monospace', fontSize: '0.85em', width: '100%' }}
          onChange={(e) => onChange({ ...block, js: e.target.value || undefined })} />
      </label>
      <div className="card" style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'var(--warn)',
        fontSize: '0.82em', color: 'var(--fg-3)', marginTop: '0.6em' }}>
        Rendered in a sandboxed iframe with <code>sandbox=&quot;allow-scripts&quot;</code> —
        no access to parent window, no cookies, no same-origin. Safe for arbitrary content.
      </div>
    </div>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
